import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { WATCH_PARTY_EVENTS } from '@/config/watchPartyData';
import { SOCIAL } from '@/config/assets';
import { Instagram, Youtube, ExternalLink, Send, ArrowDown } from 'lucide-react';

function useCountdown(targetIso, durationMin) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(i); }, []);
  const target = new Date(targetIso).getTime();
  const diff = target - now;
  const elapsed = now - target;
  if (diff > 0) {
    const d = Math.floor(diff / 86400000); const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000); const s = Math.floor((diff % 60000) / 1000);
    return { phase: 'upcoming', text: `${d > 0 ? `${d} day${d > 1 ? 's' : ''}, ` : ''}${h} hour${h !== 1 ? 's' : ''}, ${String(m).padStart(2, '0')} minute${m !== 1 ? 's' : ''}` };
  }
  if (elapsed < 10000) return { phase: 'press-play', text: 'PRESS PLAY NOW' };
  if (elapsed < (durationMin + 30) * 60000) {
    const em = Math.floor(elapsed / 60000); const es = Math.floor((elapsed % 60000) / 1000);
    return { phase: 'live', text: `${em}:${String(es).padStart(2, '0')} into the film` };
  }
  return { phase: 'ended', text: 'This room has closed' };
}

export default function WatchPartyPage() {
  const { slug } = useParams();
  const event = WATCH_PARTY_EVENTS[slug];
  const [session, setSession] = useState(null);
  const [rsvpEmail, setRsvpEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [rsvpError, setRsvpError] = useState('');
  const [hasRsvp, setHasRsvp] = useState(false);
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const chatRef = useRef(null);
  const msgTimestamps = useRef([]);
  const countdown = useCountdown(event?.scheduledAt, event?.durationMinutes || 94);
  const isArchived = countdown.phase === 'ended';

  // Auth: check session on mount + listen for magic link callback
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) handleSessionReady(data.session);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((ev, s) => {
      if (s) handleSessionReady(s);
    });
    return () => listener?.subscription?.unsubscribe();
  }, [slug]);

  // When session is ready, set state + ensure RSVP row has user_id
  const handleSessionReady = async (s) => {
    setSession(s);
    setDisplayName(s.user.email.split('@')[0]);
    // Upsert RSVP with user_id now that we're authenticated
    const { error } = await supabase.from('watch_party_rsvps').upsert(
      { event_slug: slug, email: s.user.email, display_name: s.user.email.split('@')[0], user_id: s.user.id },
      { onConflict: 'event_slug,email' }
    );
    if (!error) setHasRsvp(true);
  };

  // Load messages (filter deleted_at IS NULL) + subscribe to realtime
  useEffect(() => {
    if (!slug) return;
    supabase.from('watch_party_messages').select('*').eq('event_slug', slug).is('deleted_at', null).order('created_at', { ascending: true }).limit(500)
      .then(({ data }) => { if (data) setMessages(data); });

    const channel = supabase.channel(`chat-${slug}`).on('postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'watch_party_messages', filter: `event_slug=eq.${slug}` },
      (payload) => { setMessages(prev => [...prev, payload.new]); }
    ).subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [slug]);

  // Auto-scroll
  useEffect(() => {
    if (chatRef.current && !showScrollBtn) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, showScrollBtn]);

  const handleScroll = () => {
    if (!chatRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatRef.current;
    setShowScrollBtn(scrollHeight - scrollTop - clientHeight > 100);
  };

  const scrollToBottom = () => { chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' }); setShowScrollBtn(false); };

  // Step 1: Collect email + send magic link (no RSVP row yet — no user_id)
  const handleSendMagicLink = async (e) => {
    e.preventDefault();
    if (!rsvpEmail) return;
    setRsvpLoading(true); setRsvpError('');
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: rsvpEmail,
        options: { emailRedirectTo: `${window.location.origin}/watch-party/${slug}` },
      });
      if (error) { setRsvpError(error.message); } else { setMagicLinkSent(true); }
    } catch { setRsvpError('Something went wrong. Try again.'); }
    setRsvpLoading(false);
  };

  // Send message (rate-limited)
  const sendMessage = useCallback(async () => {
    if (!msgInput.trim() || !session || !hasRsvp || isArchived) return;
    const now = Date.now();
    msgTimestamps.current = msgTimestamps.current.filter(t => now - t < 10000);
    if (msgTimestamps.current.length >= 5) return;
    msgTimestamps.current.push(now);
    const body = msgInput.trim().slice(0, 500);
    setMsgInput('');
    await supabase.from('watch_party_messages').insert({
      event_slug: slug, user_id: session.user.id,
      display_name: displayName || session.user.email.split('@')[0], body
    });
  }, [msgInput, session, slug, displayName, hasRsvp, isArchived]);

  if (!event) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a', color: '#888173' }}><p className="italic" style={{ fontFamily: 'Georgia, serif' }}>Event not found.</p></div>;

  const chatUnlocked = session && hasRsvp;

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0a', color: '#E8E2D2' }}>
      {/* Nav */}
      <header className="px-5 md:px-10 lg:px-20 h-14 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(201,169,97,0.08)' }}>
        <Link to="/" className="font-['Playfair_Display'] italic text-lg at-shimmer" style={{ textDecoration: 'none' }}>AT.</Link>
        <div className="flex items-center gap-5">
          <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" style={{ color: '#888173' }}><Instagram className="w-3.5 h-3.5" strokeWidth={1.5} /></a>
          <a href={SOCIAL.youtube} target="_blank" rel="noopener noreferrer" style={{ color: '#888173' }}><Youtube className="w-3.5 h-3.5" strokeWidth={1.5} /></a>
          <a href={SOCIAL.imdb} target="_blank" rel="noopener noreferrer" style={{ color: '#888173' }}><ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} /></a>
        </div>
      </header>

      {/* Hero */}
      <div className="px-5 md:px-10 lg:px-20 pt-16 md:pt-24 pb-12 md:pb-16 max-w-3xl mx-auto text-center">
        <h1 className="text-[32px] md:text-[42px] italic leading-tight mb-2" style={{ color: '#C9A961', fontFamily: 'Georgia, serif' }}>
          {event.title}. <span style={{ color: '#888173' }}>{event.subtitle}.</span>
        </h1>
        <p className="text-[13px] tracking-[0.22em] uppercase mt-4 mb-8" style={{ color: '#888173', fontFamily: 'Inter, sans-serif' }}>
          Wednesday, June 24, 2026 · 9:00 PM IST
        </p>

        {/* Countdown */}
        <div className="mb-10">
          {countdown.phase === 'upcoming' && <p className="text-[24px] md:text-[32px] italic" style={{ color: '#C9A961', fontFamily: 'Georgia, serif' }}>The film begins in {countdown.text}.</p>}
          {countdown.phase === 'press-play' && <p className="text-[28px] md:text-[36px] italic font-semibold animate-pulse" style={{ color: '#C9A961', fontFamily: 'Georgia, serif' }}>PRESS PLAY NOW</p>}
          {countdown.phase === 'live' && <p className="text-[24px] md:text-[30px] italic" style={{ color: '#C9A961', fontFamily: 'Georgia, serif' }}>Live now — {countdown.text}</p>}
          {countdown.phase === 'ended' && <p className="text-[20px] italic" style={{ color: '#888173', fontFamily: 'Georgia, serif' }}>This room closed. The conversation lives on below.</p>}
        </div>

        <p className="text-[15px] italic mb-8" style={{ color: '#888173', fontFamily: 'Georgia, serif' }}>{event.contextLine}</p>

        <a href={event.watchUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[13px] tracking-wide transition-all hover:bg-white/5 mb-10" style={{ border: '1px solid rgba(201,169,97,0.25)', color: '#C9A961', fontFamily: 'Inter, sans-serif' }}>
          Watch on {event.watchPlatform}
        </a>

        {/* Auth states */}
        {!session && !magicLinkSent && (
          <div className="max-w-sm mx-auto mt-4">
            <p className="text-[12px] tracking-[0.18em] uppercase mb-4" style={{ color: '#888173', fontFamily: 'Inter, sans-serif' }}>RSVP to join the room</p>
            <form onSubmit={handleSendMagicLink} className="flex gap-2">
              <input type="email" value={rsvpEmail} onChange={e => setRsvpEmail(e.target.value)} placeholder="Your email" required className="flex-1 bg-transparent border-0 border-b px-0 py-2.5 text-sm focus:ring-0 focus:outline-none" style={{ borderColor: 'rgba(201,169,97,0.15)', color: '#E8E2D2', fontFamily: 'Inter, sans-serif' }} />
              <button type="submit" disabled={rsvpLoading} className="px-5 py-2.5 rounded-full text-[12px] tracking-wide transition-all disabled:opacity-50" style={{ background: '#C9A961', color: '#0a0a0a', fontFamily: 'Inter, sans-serif' }}>
                {rsvpLoading ? '...' : 'RSVP'}
              </button>
            </form>
            {rsvpError && <p className="text-xs mt-2" style={{ color: '#993C1D' }}>{rsvpError}</p>}
          </div>
        )}
        {!session && magicLinkSent && (
          <div className="max-w-sm mx-auto mt-4">
            <p className="text-[14px] italic" style={{ color: '#C9A961', fontFamily: 'Georgia, serif' }}>
              Magic link sent to {rsvpEmail}. Check your inbox and click the link to enter the room.
            </p>
          </div>
        )}
        {session && hasRsvp && (
          <p className="text-[13px] mt-4" style={{ color: '#888173' }}>
            In the room as <span style={{ color: '#C9A961' }}>{displayName}</span>
          </p>
        )}
        {session && !hasRsvp && (
          <p className="text-[13px] mt-4" style={{ color: '#888173' }}>Syncing your RSVP...</p>
        )}
      </div>

      {/* Chat */}
      <div className="max-w-[720px] mx-auto px-5 pb-20">
        {(chatUnlocked || isArchived) && (
          <div className="rounded-sm overflow-hidden" style={{ border: '1px solid rgba(201,169,97,0.1)' }}>
            <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(201,169,97,0.08)' }}>
              <span className="text-[11px] tracking-[0.22em] uppercase" style={{ color: '#888173', fontFamily: 'Inter, sans-serif' }}>
                {isArchived ? 'Archive' : 'Live Room'} · {messages.length} message{messages.length !== 1 ? 's' : ''}
              </span>
              {countdown.phase === 'live' && <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#993C1D' }} />}
            </div>

            <div ref={chatRef} onScroll={handleScroll} className="overflow-y-auto px-4 py-4 space-y-4" style={{ maxHeight: '50vh', minHeight: '200px' }}>
              {messages.length === 0 && (
                <p className="text-center text-[13px] italic py-8" style={{ color: '#888173', fontFamily: 'Georgia, serif' }}>
                  {isArchived ? 'No messages in this room.' : 'The room is quiet. Say something.'}
                </p>
              )}
              {messages.map(msg => (
                <div key={msg.id}>
                  <div className="flex items-baseline gap-2 mb-0.5">
                    <span className="text-[13px] italic" style={{ color: '#C9A961', fontFamily: 'Georgia, serif' }}>{msg.display_name}</span>
                    <span className="text-[10px]" style={{ color: '#5C5851' }}>{new Date(msg.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                  </div>
                  <p className="text-[14px] leading-relaxed" style={{ color: '#E8E2D2', fontFamily: '-apple-system, sans-serif' }}>{msg.body}</p>
                </div>
              ))}
            </div>

            {showScrollBtn && (
              <div className="flex justify-center py-2" style={{ borderTop: '1px solid rgba(201,169,97,0.06)' }}>
                <button onClick={scrollToBottom} className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full" style={{ color: '#C9A961', border: '1px solid rgba(201,169,97,0.15)' }}>
                  <ArrowDown className="w-3 h-3" /> Scroll to latest
                </button>
              </div>
            )}

            {chatUnlocked && !isArchived && (
              <div className="flex gap-2 px-4 py-3" style={{ borderTop: '1px solid rgba(201,169,97,0.08)' }}>
                <input value={msgInput} onChange={e => setMsgInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} maxLength={500} placeholder="Say something..." className="flex-1 bg-transparent border-0 px-0 py-1.5 text-sm focus:ring-0 focus:outline-none italic" style={{ color: '#E8E2D2', fontFamily: 'Georgia, serif' }} />
                <button onClick={sendMessage} className="p-2 rounded-full transition-all hover:bg-white/5" style={{ color: '#C9A961' }}>
                  <Send className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            )}
          </div>
        )}

        {!chatUnlocked && !isArchived && (
          <p className="text-center text-[13px] italic py-10" style={{ color: '#888173', fontFamily: 'Georgia, serif' }}>
            RSVP and click your magic link to enter the room.
          </p>
        )}
      </div>
    </div>
  );
}
